import { authErrorHandler } from '@/services/error/authHandler.js';

/**
 * Universal method for retrieving user connections.
 * @param {Object} params - Request parameters.
 * @param {string} params.userId - User ID.
 * @param {string} params.type - Connection type (followers/following).
 * @param {number} [params.offset=0] - Offset for pagination.
 * @param {number} [params.limit=20] - Limit for pagination.
 * @param {boolean} [params.withUnreadPosts] - Whether to return only users with unread posts.
 * @returns {Promise<Object|null>} - User followers/following data or null in case of error.
 */
/**
 * @param {{ userId?: string; type?: string; offset?: number; limit?: number; withUnreadPosts?: boolean } | undefined} params
 */
export const fetchUserConnections = async (params = {}) => {
  try {
    if (!params.userId || params.userId === 'null') {
      console.error('userId is invalid:', params.userId);
      return null;
    }
    if (!params.type || params.type === 'null') {
      console.error('type is invalid:', params.type);
      return null;
    }

    const { userId, type, ...queryParams } = params;

    const filteredParams = Object.fromEntries(
      Object.entries({
        ...queryParams,
        offset: queryParams.offset || 0,
        limit: queryParams.limit || 15,
        withUnreadPosts: queryParams.withUnreadPosts,
      }).filter(([_, value]) => value !== undefined),
    );

    if (!userId || userId === 'null') {
      console.error('userId is invalid:', new Error().stack);
      return null;
    }

    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(filteredParams)) {
      sp.append(k, String(v));
    }
    const searchParams = sp.toString();
    const url = `/api/users/${userId}/${type}?${searchParams}`;

    const response = await fetch(url, {
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (response.status === 401 || response.status === 403) {
      authErrorHandler(response);
      return null;
    }

    const data = await response.json();

    if (response.ok) {
      return data;
    } else {
      console.error(`Error fetching ${type}:`, data);
      return null;
    }
  } catch (error) {
    console.error(`Error during ${params.type} API call:`, error);
    return null;
  }
};

/**
 * Universal method for managing subscriptions (follow/unfollow).
 * @param {string} userId - User ID or Channel ID.
 * @param {string} action - Action type ('follow' or 'unfollow').
 * @param {boolean} [negative] - For follow action: whether this is a "skeptical" subscription.
 * @param {boolean} [isChannel] - If true, subscribe to a channel.
 * @returns {Promise<void>} - Data of the user/channel being followed/unfollowed or null in case of error.
 */
export const manageFollowState = async (userId, action, negative = false, isChannel = false) => {
  if (!userId || userId === 'null') {
    console.error('id is invalid:', new Error().stack);
    return null;
  }

  let url, body;

  if (isChannel) {
    if (action === 'unfollow') {
      url = '/api/users/unfollow-channel';
      body = JSON.stringify({ channelId: userId });
    } else if (action === 'follow') {
      url = '/api/users/follow-channel';
      body = JSON.stringify({
        channelId: userId,
        followType: negative ? 'monitor' : 'follow',
      });
    } else {
      throw new Error('Unknown action for channel');
    }
  } else {
    if (action === 'unfollow') {
      url = `/api/users/${userId}/unfollow`;
      body = undefined;
    } else if (action === 'follow') {
      url = `/api/users/${userId}/follow`;
      body = JSON.stringify({ negative });
    } else {
      throw new Error('Unknown action for user');
    }
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (response.status === 401 || response.status === 403) {
      authErrorHandler(response);
      return null;
    }

    const data = await response.json();

    if (response.ok) {
      return data;
    } else {
      console.error(`Error performing "${action}" action:`, data);
      return null;
    }
  } catch (error) {
    console.error(`Error performing "${action}" action:`, error);
    throw error;
  }
};
